import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EntityStateType, ItemState, ItemStateBody, ItemStateType } from '@compiere-ws/models/apiz-domains';
import { CompiereDataFieldType } from '@compiere-ws/models/compiere-data-json';
import { environment } from 'environments/environment';
import { Observable, Subscriber } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FieldStateService {
  #http = inject(HttpClient);

  getEntityStateUrl(entity: string, id: number): string {
    return `${environment.config.backend.ws.url}/ui/${entity}/${id}/state`;
  }

  getEntityState<T extends ItemState | undefined>({
    fieldType,
    id,
    ...request
  }: ItemStateBody & { fieldType: CompiereDataFieldType; id: number }): Observable<T> {
    const entityId = id;
    const entity = ((): EntityStateType => {
      if (fieldType === CompiereDataFieldType.FIELD) {
        return EntityStateType.FIELD;
      }

      if (fieldType === CompiereDataFieldType.FORM_ITEM) {
        return EntityStateType.FORM_DETAIL_ITEM;
      }

      if (fieldType === CompiereDataFieldType.PROCESS_PARA) {
        return EntityStateType.PROCESS_PARAM;
      }
    })();

    // window fields: asked together (one request for every field asking the same states with the same context in
    // this turn, /ui/fields/state); the others alone. Both share a request still running for the same question.
    if (entity === EntityStateType.FIELD) {
      return this.#batched<T>(entityId, request.states, request.windowCtx);
    }
    return this.#shared<T>(
      `${entity}|${entityId}|${request.states?.join(',')}|${JSON.stringify(request.windowCtx ?? {})}`,
      () => this.#http.post<T>(this.getEntityStateUrl(entity, entityId), request)
    );
  }

  //#region one request for the same question, one request for many fields
  readonly #inFlight = new Map<string, Observable<any>>();

  #shared<T>(key: string, request: () => Observable<T>): Observable<T> {
    let shared = this.#inFlight.get(key) as Observable<T>;
    if (!shared) {
      shared = request().pipe(
        finalize(() => this.#inFlight.delete(key)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
      this.#inFlight.set(key, shared);
    }
    return shared;
  }

  /** fields waiting for the batch of their question (states + context), sent at the end of this turn */
  readonly #batches = new Map<
    string,
    { states: ItemStateType[]; windowCtx: Record<string, any>; waiting: Map<number, Subscriber<any>[]> }
  >();

  #batched<T>(fieldId: number, states: ItemStateType[], windowCtx: Record<string, any>): Observable<T> {
    return new Observable<T>((subscriber) => {
      const key = `${[...(states ?? [])].sort().join(',')}|${JSON.stringify(windowCtx ?? {})}`;
      let batch = this.#batches.get(key);
      if (!batch) {
        batch = { states, windowCtx, waiting: new Map() };
        this.#batches.set(key, batch);
        queueMicrotask(() => this.#flush(key));
      }
      const waiting = batch.waiting.get(fieldId) ?? [];
      waiting.push(subscriber);
      batch.waiting.set(fieldId, waiting);
      return () => {
        // gone before the batch left (switchMap, destroyed field): not waited for any more
        const list = batch.waiting.get(fieldId);
        if (list) {
          const rest = list.filter((s) => s !== subscriber);
          rest.length ? batch.waiting.set(fieldId, rest) : batch.waiting.delete(fieldId);
        }
      };
    });
  }

  #flush(key: string): void {
    const batch = this.#batches.get(key);
    this.#batches.delete(key);
    const fieldIds = [...(batch?.waiting.keys() ?? [])];
    if (!fieldIds.length) return;
    const answer = (id: number, value: any) =>
      (batch.waiting.get(id) ?? []).forEach((s) => {
        s.next(value);
        s.complete();
      });
    const fail = (id: number, error: any) => (batch.waiting.get(id) ?? []).forEach((s) => s.error(error));
    if (fieldIds.length === 1) {
      // one field: the single endpoint (same answer and errors as before)
      const id = fieldIds[0];
      this.#shared(`field|${id}|${key}`, () =>
        this.#http.post<any>(this.getEntityStateUrl(EntityStateType.FIELD, id), {
          states: batch.states,
          windowCtx: batch.windowCtx,
        })
      ).subscribe({ next: (value) => answer(id, value), error: (error) => fail(id, error) });
      return;
    }
    const fieldError = (id: number, error: any) =>
      new HttpErrorResponse({ error, status: 500, statusText: 'Field state error', url: `fields/state#${id}` });
    this.#http
      .post<BatchFieldStateResponse>(`${environment.config.backend.ws.url}/ui/fields/state`, {
        fieldIds,
        states: batch.states,
        windowCtx: batch.windowCtx,
      })
      .subscribe({
        next: (response) => {
          for (const id of fieldIds) {
            const state = response?.results?.[id];
            state !== undefined ? answer(id, state) : fail(id, fieldError(id, response?.errors?.[id]));
          }
        },
        error: (error: HttpErrorResponse) => {
          // every field failed (400 with the errors per field) or the request itself failed
          const errors = error?.error?.errors;
          for (const id of fieldIds) fail(id, errors?.[id] ? fieldError(id, errors[id]) : error);
        },
      });
  }
  //#endregion

  getTabState<T extends ItemState | undefined>(recordId: number, request: ItemStateBody): Observable<T> {
    return this.#http.post<T>(this.getEntityStateUrl(EntityStateType.TAB, recordId), request);
  }

  getFieldState<T extends ItemState | undefined>(recordId: number, request: ItemStateBody): Observable<T> {
    return this.#http.post<T>(this.getEntityStateUrl(EntityStateType.FIELD, recordId), request);
  }

  getProcessParamState<T extends ItemState | undefined>(recordId: number, request: ItemStateBody): Observable<T> {
    return this.#http.post<T>(this.getEntityStateUrl(EntityStateType.PROCESS_PARAM, recordId), request);
  }

  getFormDetailState<T extends ItemState | undefined>(recordId: number, request: ItemStateBody): Observable<T> {
    return this.#http.post<T>(this.getEntityStateUrl(EntityStateType.FORM_DETAIL_ITEM, recordId), request);
  }
}

/** answer of /ui/fields/state: states by field id, errors by field id (207 when some failed) */
interface BatchFieldStateResponse {
  results?: Record<number, ItemState>;
  errors?: Record<number, any>;
}
